import { useMemo } from 'react'
import { MapAdapter, MapAdapterOptions } from './types'
import { MapboxAdapter } from './MapboxAdapter'
import { nullAdapter } from './NullAdapter'

// Import useMap - must be called unconditionally per Rules of Hooks
import { useMap } from '../../../pages/Explore/DeveloperMap/contexts/MapContext'

/**
 * Hook to get the appropriate map adapter based on context and configuration
 * Returns MapboxAdapter if MapContext is available, otherwise NullAdapter
 *
 * @param enableMapIntegration - Whether map integration is enabled (default: true)
 * @param options - Optional adapter configuration (e.g., marker placement callback)
 * @returns MapAdapter instance
 *
 * Note: This component requires being wrapped in MapProvider from DeveloperMap contexts.
 * The hook always calls useMap() but only uses it when enableMapIntegration is true.
 */
export const useMapAdapter = (
  enableMapIntegration: boolean = true,
  options?: MapAdapterOptions
): MapAdapter => {
  // Always call useMap unconditionally (Rules of Hooks)
  // This means SearchLocation must be used within MapProvider

  if (!enableMapIntegration) {
    return nullAdapter
  }

  const mapContext = useMap()

  // Memoize the adapter to avoid recreating on every render
  const adapter = useMemo(() => {
    // If map integration is disabled, return null adapter
    if (!enableMapIntegration) {
      return nullAdapter
    }

    // If context values are not available, return null adapter
    if (!mapContext || !mapContext.map || !mapContext.mapLoaded) {
      return nullAdapter
    }

    // Create and return Mapbox adapter with options
    return new MapboxAdapter(mapContext.map, mapContext.mapLoaded, options)
  }, [enableMapIntegration, mapContext.map, mapContext.mapLoaded, options])

  return adapter
}
